/**
 * Estatísticas derivadas dos slots URA (GET /chatbot/slots) para analytics do chatbot.
 */

function buildParentMap(slots) {
  const map = new Map();
  for (const r of slots || []) {
    const id = Number(r.id);
    if (!Number.isFinite(id)) continue;
    const rawP = r.id_pai;
    const p = rawP == null || rawP === "" ? 0 : Number(rawP);
    map.set(id, Number.isFinite(p) ? p : 0);
  }
  return map;
}

function computeMaxDepth(parentMap) {
  const memo = new Map();
  function depthOf(id, stack) {
    if (memo.has(id)) return memo.get(id);
    if (stack.has(id)) return 1;
    stack.add(id);
    const p = parentMap.get(id);
    let d;
    if (p == null || p === 0 || !parentMap.has(p)) d = 1;
    else d = depthOf(p, stack) + 1;
    stack.delete(id);
    memo.set(id, d);
    return d;
  }
  let maxD = 0;
  for (const id of parentMap.keys()) {
    maxD = Math.max(maxD, depthOf(id, new Set()));
  }
  return maxD;
}

function quickReplyCount(slot) {
  if (Array.isArray(slot.botoes)) return slot.botoes.filter((b) => String(b || "").trim()).length;
  if (slot.botoes != null && slot.botoes !== "")
    return String(slot.botoes)
      .split(",")
      .map((b) => b.trim())
      .filter(Boolean).length;
  return 0;
}

function hasSchedule(slot) {
  const keys = [
    "horario_seg_ini",
    "horario_ter_ini",
    "horario_qua_ini",
    "horario_qui_ini",
    "horario_sex_ini",
    "horario_sab_ini",
    "horario_dom_ini",
  ];
  return keys.some((k) => {
    const v = slot[k];
    return v != null && String(v).trim() !== "";
  });
}

/**
 * @param {Array<object>} slots - lista normalizada da API (botoes como array)
 * @returns {object}
 */
export function computeChatbotFlowStats(slots) {
  const list = Array.isArray(slots) ? slots : [];
  if (!list.length) {
    return {
      totalSteps: 0,
      maxDepth: 0,
      withQuickReplies: 0,
      withConditions: 0,
      withActions: 0,
      exceptionSteps: 0,
      globalSteps: 0,
      ignoreGlobalSteps: 0,
      withFile: 0,
      withSchedule: 0,
      withResponse: 0,
      tipoAcaoCounts: { none: 0, goto: 0, automation: 0, other: 0 },
      avgDirectChildren: 0,
      maxDirectChildren: 0,
      topByChildren: [],
      topIdentifiers: [],
    };
  }

  const parentMap = buildParentMap(list);
  const maxDepth = computeMaxDepth(parentMap);

  const directChildren = new Map();
  for (const s of list) {
    const pid = s.id_pai == null || s.id_pai === "" ? 0 : Number(s.id_pai);
    const p = Number.isFinite(pid) ? pid : 0;
    directChildren.set(p, (directChildren.get(p) || 0) + 1);
  }

  let withQuickReplies = 0;
  let withConditions = 0;
  let withActions = 0;
  let exceptionSteps = 0;
  let globalSteps = 0;
  let ignoreGlobalSteps = 0;
  let withFile = 0;
  let withSchedule = 0;
  let withResponse = 0;
  const tipoAcaoCounts = { none: 0, goto: 0, automation: 0, other: 0 };
  const identCounts = new Map();

  for (const s of list) {
    const id = String(s.identificador ?? "").trim();
    if (id) identCounts.set(id, (identCounts.get(id) || 0) + 1);

    if (quickReplyCount(s) > 0) withQuickReplies += 1;
    if (String(s.condicoes ?? "").trim()) withConditions += 1;
    const ta = Number(s.tipo_acao);
    if (Number.isFinite(ta) && ta > 0) {
      withActions += 1;
      if (ta === 2) tipoAcaoCounts.goto += 1;
      else if (ta === 3) tipoAcaoCounts.automation += 1;
      else tipoAcaoCounts.other += 1;
    } else {
      tipoAcaoCounts.none += 1;
    }
    if (String(s.identificador ?? "").trim() === "Exceção") exceptionSteps += 1;
    if (s.global === true || s.global === 1) globalSteps += 1;
    if (s.ignorarglobal === true || s.ignorarglobal === 1) ignoreGlobalSteps += 1;
    if (String(s.arquivo ?? "").trim()) withFile += 1;
    if (hasSchedule(s)) withSchedule += 1;
    if (String(s.resposta ?? "").trim()) withResponse += 1;
  }

  function depthForSlot(sid) {
    if (!Number.isFinite(sid) || !parentMap.has(sid)) return 1;
    let depth = 1;
    let cur = sid;
    const seen = new Set();
    for (let i = 0; i < 5000; i++) {
      if (seen.has(cur)) break;
      seen.add(cur);
      const p = parentMap.get(cur);
      if (p == null || p === 0 || !parentMap.has(p)) break;
      depth += 1;
      cur = p;
    }
    return depth;
  }

  const simplerChildren = list.map((s) => {
    const sid = Number(s.id);
    const n = Number.isFinite(sid) ? directChildren.get(sid) || 0 : 0;
    return {
      id: sid,
      identificador: String(s.identificador ?? "").trim() || `#${sid}`,
      children: n,
      depth: depthForSlot(sid),
    };
  });

  const topByChildren = [...simplerChildren]
    .filter((r) => Number.isFinite(r.id))
    .sort((a, b) => b.children - a.children)
    .slice(0, 10);

  const topIdentifiers = [...identCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name, count }));

  const slotIds = new Set(list.map((s) => Number(s.id)).filter(Number.isFinite));
  const parentIdsWithSlots = [...slotIds].filter((id) => (directChildren.get(id) || 0) > 0);
  const counts = parentIdsWithSlots.map((id) => directChildren.get(id) || 0);
  const sumChildren = counts.reduce((a, b) => a + b, 0);
  const parentsWithChildren = counts.length;
  const avgDirectChildren = parentsWithChildren > 0 ? sumChildren / parentsWithChildren : 0;
  const maxDirectChildren = counts.length ? Math.max(...counts) : 0;

  return {
    totalSteps: list.length,
    maxDepth,
    withQuickReplies,
    withConditions,
    withActions,
    exceptionSteps,
    globalSteps,
    ignoreGlobalSteps,
    withFile,
    withSchedule,
    withResponse,
    tipoAcaoCounts,
    avgDirectChildren,
    maxDirectChildren,
    topByChildren,
    topIdentifiers,
  };
}

const LEAD_STATUS_CODES = [0, 1, 2, 3, 4, 5];

export function aggregateConversationStatus(list) {
  const rows = Array.isArray(list) ? list : [];
  const m = new Map();
  for (const c of rows) {
    const n = Number(c.status);
    const k = Number.isFinite(n) ? n : "unknown";
    m.set(k, (m.get(k) || 0) + 1);
  }
  const out = LEAD_STATUS_CODES.map((code) => ({ code, count: m.get(code) || 0 }));
  if (m.has("unknown")) out.push({ code: "unknown", count: m.get("unknown") });
  return out;
}

export function aggregateChannels(list) {
  const rows = Array.isArray(list) ? list : [];
  const m = new Map();
  for (const c of rows) {
    const ch = String(c.channel || c.channelName || "").trim() || "—";
    m.set(ch, (m.get(ch) || 0) + 1);
  }
  return [...m.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
}

/**
 * Une séries diárias retornadas pela API (`activity`, `created`) em uma linha por dia (últimos `days`).
 * @param {{ day: string, count: number }[]} activity
 * @param {{ day: string, count: number }[]} created
 */
export function mergeChatbotDailySeries(activity, created, days = 30) {
  const actMap = new Map((activity || []).map((r) => [String(r.day).slice(0, 10), Number(r.count) || 0]));
  const creMap = new Map((created || []).map((r) => [String(r.day).slice(0, 10), Number(r.count) || 0]));
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const keys = [];
  const safeDays = Math.min(Math.max(Number(days) || 30, 1), 90);
  for (let i = safeDays - 1; i >= 0; i -= 1) {
    const x = new Date(d);
    x.setDate(x.getDate() - i);
    keys.push(x.toISOString().slice(0, 10));
  }
  return keys.map((day) => ({
    day,
    activityCount: actMap.get(day) ?? 0,
    createdCount: creMap.get(day) ?? 0,
  }));
}
